import { useEffect } from "react";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { applyBranding, loadBrandingForSlug } from "@/lib/branding";

/*
 * Layout route shared by every /<tenantSlug>/* page on the portal.
 * Vanity-domain visitors are already branded at cold load via
 * `loadBrandingForCurrentHost` in main.tsx — but vanity-host lookup
 * returns null for the platform `portal.{base}` host where the tenant
 * identity lives in the URL path, not the hostname. This layout fills
 * that gap by re-running the resolver against the slug as soon as the
 * tenant routes mount, so a primary-colour or logo change pushed from
 * Settings -> Public surface -> Branding shows up on every tenant
 * page regardless of which host the visitor arrived on.
 *
 * Public-surface spec: docs/product/public_surface.md §8.
 */
export const Route = createFileRoute("/$tenantSlug")({
  component: TenantLayout,
});

function TenantLayout() {
  const { tenantSlug } = Route.useParams();
  const { data } = useQuery({
    queryKey: ["portal-branding", tenantSlug] as const,
    queryFn: () => loadBrandingForSlug(tenantSlug),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data?.branding) applyBranding(data.branding);
  }, [data]);

  return <Outlet />;
}
